<template>
  <v-container fluid class="pa-0 h-100 w-100">
    <v-toolbar flat>
      <v-toolbar-title style="align-items: center; display: flex">
        <v-icon left>
          mdi-image-multiple
        </v-icon>{{ $t('alignment.pageTitle') }}
      </v-toolbar-title>
      <v-divider class="mx-4" inset vertical />
      <!-- 선택 해제 버튼 -->
      <v-btn
        v-if="!getShowExplorer && getSelectedPolygonCount > 0"
        outlined
        small
        class="solvit-crud-outline-btn mr-2"
        @click="clearAllSelections"
      >
        <v-icon left small>
          mdi-close-circle
        </v-icon>
        {{ $t('alignment.deselectWithCount', { count: getSelectedPolygonCount }) }}
      </v-btn>
    </v-toolbar>

    <div class="page-content">
      <div class="main-content">
        <div class="navigation-sidebar">
          <v-card flat class="d-flex flex-column fill-height">
            <div v-show="getShowExplorer" class="sidebar_list">
              <div
                v-if="getLoading"
                class="image-alignment-rail-stack__empty"
                style="overflow: hidden"
              >
                <RailListSkeleton variant="tree-row" :count="10" root-class="px-2" />
              </div>
              <!-- 프로젝트/사이트/보고서 목록 -->
              <template v-else>
                <v-list-group
                  v-for="project in getProjects"
                  :key="project.id"
                  no-action
                  prepend-icon="mdi-projector-screen-outline"
                >
                  <template slot="activator">
                    <v-list-item-title>{{ project.name }}</v-list-item-title>
                  </template>
                  <!-- 사이트 목록 -->
                  <v-list-group
                    v-for="site in project.children"
                    :key="site.id"
                    class="list_area"
                    no-action
                    sub-group
                    prepend-icon="mdi-solar-panel"
                    @click.stop.prevent="handleSelectSite(site)"
                  >
                    <template slot="activator">
                      <v-list-item-content>
                        <v-list-item-title>{{ site.name }}</v-list-item-title>
                      </v-list-item-content>
                    </template>
                    <!-- 날짜별 보고서 목록 -->
                    <v-list-group
                      v-for="day in site.children"
                      :key="day.name"
                      class="list_day"
                      sub-group
                      no-action
                      prepend-icon="mdi-calendar-search-outline"
                    >
                      <template slot="activator">
                        <v-list-item-content>
                          <v-list-item-title>{{ day.name }}</v-list-item-title>
                        </v-list-item-content>
                      </template>
                      <!-- 보고서 목록 -->
                      <v-list-item-group>
                        <v-list-item
                          v-for="report in day.children"
                          :key="report.id"
                          link
                          @click.stop.prevent="handleSelectReport(site, report)"
                        >
                          <v-progress-linear
                            v-show="getLoading && report.id === tempSelectedReportId"
                            indeterminate
                            style="position: absolute; bottom: 0; left: 0"
                          />
                          <v-list-item-title>{{ report.name }}</v-list-item-title>
                        </v-list-item>
                      </v-list-item-group>
                    </v-list-group>
                  </v-list-group>
                </v-list-group>
              </template>
            </div>
            <!-- 이미지 리스트 (showExplorer가 false일 때) -->
            <div v-show="!getShowExplorer" class="image-alignment-rail-stack">
              <div class="d-flex flex-column image-alignment-rail-stack__inner">
                <!-- 헤더: 보고서명 → 사이트·일자 → 그 아래 액션·이미지 목록 -->
                <v-card
                  ref="stickyCard"
                  flat
                  color="transparent"
                  class="d-flex justify-space-between align-start flex-shrink-0 solvit-rail-subheader solvit-rail-subheader--emphasis solvit-rail-subheader--compact"
                >
                  <div
                    class="flex-grow-1 solvit-rail-subheader__body"
                    :title="imageAlignmentRailTitleFull"
                  >
                    <div class="solvit-rail-subheader__report text--primary text-truncate">
                      {{ getSelectedReport?.name }}
                    </div>
                    <div class="solvit-rail-subheader__meta text--secondary text-truncate">
                      <span>{{ getSelectedSite?.name }}</span>
                      <span v-if="imageAlignmentReportExamineDate"> · {{ imageAlignmentReportExamineDate }}</span>
                    </div>
                  </div>
                  <v-btn class="flex-shrink-0 solvit-rail-subheader__back" icon @click.stop="handleReturnToNavigation">
                    <BackArrowIcon />
                  </v-btn>
                </v-card>
                <!-- 액션 버튼들 (이미지 리스트 근처) -->
                <v-card v-if="!getShowExplorer && getThermalImages && getThermalImages.length > 0" class="d-flex justify-space-between align-center flex-shrink-0 px-2 py-1 action-buttons-container" flat>
                  <v-btn
                    outlined
                    small
                    :disabled="getSelectedImages.length === 0"
                    :class="[
                      'mr-2 action-button',
                      getSelectedImages.length > 0
                        ? 'solvit-crud-outline-btn solvit-crud-outline-btn--primary'
                        : 'solvit-crud-outline-btn'
                    ]"
                    @click="showSelectedImages"
                  >
                    <v-icon left>
                      {{ getSelectedImages.length > 0 ? 'mdi-map-marker-multiple' : 'mdi-map-marker-off' }}
                    </v-icon>
                    {{
                      getSelectedImages.length > 0
                        ? $t('alignment.showOnMap', {
                          count: getSelectedImages.length
                        })
                        : $t('alignment.noSelection')
                    }}
                  </v-btn>
                  <div class="d-flex align-center">
                    <v-btn
                      v-if="getSelectedImages.length > 0"
                      outlined
                      small
                      class="solvit-crud-outline-btn clear-all-button"
                      @click="clearAllSelections"
                    >
                      <v-icon left>
                        mdi-close-circle
                      </v-icon>{{ $t('alignment.clearAll') }}
                    </v-btn>
                  </div>
                </v-card>

                <!-- 이미지 탐색기 -->
                <div v-if="!getShowExplorer && getThermalImages && getThermalImages.length > 0" class="image-list-container">
                  <ImageListUI
                    :images="getThermalImages"
                    :selected-index="selectedImageIndex"
                    :hovered-index="hoveredImageIndex"
                    style="width: 100%; height: 100%"
                    @image-multi-select="handleImageMultiSelect"
                    @image-toggle="handleImageToggle"
                    @image-edit="handleImageEdit"
                    @mouse-enter="handleMouseEnter"
                    @mouse-leave="handleMouseLeave"
                    @move-up="handleImageListMoveUp"
                    @move-down="handleImageListMoveDown"
                  />
                </div>

                <!-- 이미지 데이터 로딩 중 또는 없음 -->
                <div v-else-if="!getShowExplorer" class="d-flex align-center justify-center image-alignment-rail-stack__empty">
                  <RailListSkeleton
                    v-if="getLoading"
                    variant="image-row"
                    :count="8"
                    root-class="w-100"
                  />
                  <div v-else class="text-h6 secondary--text">
                    이미지 데이터가 없습니다
                  </div>
                </div>
              </div>
            </div>
          </v-card>
        </div>

        <!-- Map Section - GeoImagePolygonTest 방식으로 대체 -->
        <div class="map-container">
          <div id="image-alignment-map" class="map" />
        </div>
      </div>
    </div>
  </v-container>
</template>

<script>
import { mapGetters, mapActions } from 'vuex';
import ImageListUI from './ImageListUI.vue';
import BackArrowIcon from '../common/BackArrowIcon.vue';
import { formatDate } from '@/utils/fileDownloadUtils';

// OpenLayers 라이브러리 import
import Map from 'ol/Map.js';
import View from 'ol/View.js';
import TileLayer from 'ol/layer/Tile.js';
import TileWMS from 'ol/source/TileWMS.js';
import { defaults } from 'ol/interaction/defaults';

// ImageAlignmentManager를 사용
import { ImagePolygonMapManager } from '../map/ImagePolygonMapManager.js';
import { log } from '../../utils/logger.js';
// 서버 좌표 유틸리티 import
// displayServerImageCoordinates는 ImagePolygonMapManager에서 폴리곤을 처리하므로 불필요

export default {
  name: 'ImageAlignmentPage',
  components: {
    ImageListUI,
    BackArrowIcon,
    RailListSkeleton: () => import('@/components/common/RailListSkeleton.vue')
  },

  data() {
    return {
      // 지도 관련 (GeoImagePolygonTest 방식)
      map: null,
      imageManager: null,
      imageList: {},

      // selectedImages는 store에서 관리하므로 제거
      selectedImageId: null,  // 선택된 이미지 ID (GeoImagePolygonTest 방식)
      tempSelectedReportId: null,
      selectedImageIndex: -1,
      hoveredImageIndex: -1,
      isSelectActive: false,
      hasChanges: false
    };
  },

  watch: {
    getSelectedImages: {
      handler(newSelectedImages) {
        console.log('[ImageAlignmentPage] 선택된 이미지개수', newSelectedImages);
      },
      deep: true
    },
    '$store.getters.siteData.getSiteDetails': {
      handler(newSiteDetails) {
        if (newSiteDetails && this.map) {
          console.log('[ImageAlignmentPage] 사이트 상세 정보 변경 감지:', newSiteDetails);
          this.moveToSite(newSiteDetails);
        }
      },
      deep: true
    }
  },

  computed: {
    ...mapGetters({
      getProjects: 'imageAlignment/getProjects',
      getSelectedSite: 'imageAlignment/getSelectedSite',
      getSelectedReport: 'imageAlignment/getSelectedReport',
      getShowExplorer: 'imageAlignment/getShowExplorer',
      getLoading: 'imageAlignment/getLoading',
      getThermalImages: 'imageAlignment/getThermalImages',
      getSelectedImages: 'imageAlignment/getSelectedImages'
    }),

    /**
     * 선택된 폴리곤 개수 가져오기 (computed로 변경하여 반응성 보장)
     */
    getSelectedPolygonCount() {
      return this.imageManager ? this.imageManager.getSelectedPolygonCount : 0;
    },
    imageAlignmentReportExamineDate() {
      return formatDate(this.getSelectedReport?.examineDate);
    },
    imageAlignmentRailTitleFull() {
      const site = this.getSelectedSite?.name || '';
      const rep = this.getSelectedReport?.name || '';
      const d = this.imageAlignmentReportExamineDate;
      let s = site && rep ? `${site} > ${rep}` : rep || site;
      if (d) {
        s = s ? `${s} (${d})` : d;
      }
      return s;
    }
  },

  methods: {
    ...mapActions({
      selectSite: 'imageAlignment/selectSite',
      selectReport: 'imageAlignment/selectReport',
      returnToNavigation: 'imageAlignment/returnToNavigation',
      setShowExplorer: 'imageAlignment/setShowExplorer',
      setLoading: 'imageAlignment/setLoading',
      setSelectedImages: 'imageAlignment/setSelectedImages'
    }),

    // 패널플래닝과 동일한 방식의 지도 초기화
    async initMap() {

      const geoServer = process.env.VUE_APP_GEO_SERVER_URL + '/wms';
      console.log('[ImageAlignmentPage] WMS 서버 URL:', geoServer);

      // 패널플래닝과 동일한 View 설정
      this.view = new View({
        center: [126.9780, 37.5665], // 서울 좌표 (EPSG:4326 WGS84)
        zoom: 20,
        projection: 'EPSG:4326',
        // 더블클릭 줌 비활성화
        doubleClickZoom: false
      });

      // 패널플래닝과 동일한 Map 설정
      this.map = new Map({
        target: 'image-alignment-map',
        interactions: defaults({ doubleClickZoom: false }),
        layers: [
          new TileLayer({
            className: 'dotman.solar.map.satellite',
            visible: true,
            source: new TileWMS({
              url: geoServer,
              params: {
                'FORMAT': 'image/png',
                'VERSION': '1.1.1',
                'tiled': true,
                'LAYERS': 'solar-plant:Satellite',
                'SRS': 'EPSG:4326',
                'exceptions': 'application/vnd.ogc.se_inimage'
              }
            }),
            name: 'Satellite'
          }),
          // 패널플래닝과 동일한 정사이미지 레이어 추가
          new TileLayer({
            visible: true, // 기본적으로 표시
            source: new TileWMS({
              url: geoServer,
              params: {
                'FORMAT': 'image/png',
                'VERSION': '1.1.1',
                'tiled': true,
                'LAYERS': 'solar-plant:site_image_grp_layer',
                'SRS': 'EPSG:4326',
                'exceptions': 'application/vnd.ogc.se_inimage'
              }
            }),
            name: 'SiteImage'
          })
        ],
        view: this.view
      });

      try {
        // ImagePolygonMapManager가 지도 컨텍스트 가지고있음
        this.imageManager = new ImagePolygonMapManager(this.map);
      } catch (error) {
        console.error('[ImageAlignmentPage] ImagePolygonMapManager 초기화 실패:', error);
        this.imageManager = null;
      }
    },
    // 선택된 이미지들만 지도에 표시 (서버 좌표 필터링)
    async updateSelectedImagesOnMap() {
      try {
        const imagesWithCoordinates = this.getSelectedImages.filter(image => {
          const hasCoordinates = image.imgPosCoords &&
            image.imgPosCoords.coordinates &&
            image.imgPosCoords.coordinates.length > 0;

          if (!hasCoordinates) {
            log.warn('ImageAlignmentPage', '서버 좌표 없음', { id: image.id, name: image.imgName });
          }
          return hasCoordinates;
        });

        log.info('ImageAlignmentPage', '서버 좌표 필터링 결과', {
          totalSelected: this.getSelectedImages.length,
          withCoordinates: imagesWithCoordinates.length
        });

        if (imagesWithCoordinates.length === 0) {
          log.warn('ImageAlignmentPage', '서버 좌표가 있는 이미지가 없습니다');
          return;
        }

        // 2단계: 기존 지도에 표시된 모든 이미지 제거
        this.imageManager.clearAll();
        this.imageList = {};

        // 3단계: 필터링된 이미지들로 이미지-폴리곤 쌍 생성
        this.imageManager.createImagePolygonPairs(imagesWithCoordinates);

        // 4단계: 이미지 리스트 업데이트
        this.imageList = this.imageManager.getAllPolygonInfo();

        // 5단계: 선택된 이미지들의 영역으로 지도 이동
        if (imagesWithCoordinates.length > 0) {
          this.centerMapOnSelectedImages(imagesWithCoordinates);
        }

        log.complete('ImageAlignmentPage', '선택된 이미지들 지도 표시', {
          displayedCount: imagesWithCoordinates.length,
          polygonCount: this.imageManager.getPolygonCount()
        });

      } catch (error) {
        log.error('ImageAlignmentPage', '선택된 이미지들 표시', error);
      }
    },

    // 사이트로 지도 이동
    moveToSite(siteDetails) {
      if (!this.map || !siteDetails?.location?.coordinates) {
        return;
      }

      const coordinates = siteDetails.location.coordinates;
      this.map.getView().animate({
        center: coordinates,
        zoom: 15
      });
    },

    cleanup() {
      if (this.imageManager) {
        this.imageManager.clearAll();
      }

    },

    // 보고서 선택
    async handleSelectReport(site, report) {
      this.tempSelectedReportId = report.id;
      await this.selectReport(report);

      // 이미지 리스트 표시를 위해 showExplorer를 false로 설정
      this.setShowExplorer(false);

      log.start('ImageAlignmentPage', '보고서 선택 완료', {
        siteName: site.name,
        reportName: report.name,
        reportId: report.id
      });
    },

    // Show 버튼 클릭 처리 (일반 버튼 방식)
    showSelectedImages() {
      log.start('ImageAlignmentPage', 'Show 버튼 클릭', {
        selectedCount: this.getSelectedImages?.length || 0
      });

      if (!this.getSelectedImages || this.getSelectedImages.length === 0) {
        log.warn('ImageAlignmentPage', '선택된 이미지가 없습니다. 이미지를 먼저 선택해주세요.');
        return;
      }

      // 매번 초기화 후 선택된 이미지들 표시
      log.info('ImageAlignmentPage', '지도 초기화 후 선택된 이미지들을 표시합니다', {
        selectedCount: this.getSelectedImages.length,
        selectedImageIds: this.getSelectedImages.map(img => img.id),
        selectedImageNames: this.getSelectedImages.map(img => img.imgName)
      });

      // 1단계: 지도에서 모든 이미지 제거
      this.imageManager?.clearAll();
      this.imageList = {};

      // 2단계: 선택된 이미지들만 지도에 표시
      this.updateSelectedImagesOnMap();

      log.complete('ImageAlignmentPage', 'Show 버튼 클릭');
    },
    // 이미지 리스트 닫기 (BackArrow 클릭)
    handleReturnToNavigation() {
      this.returnToNavigation();
      this.setSelectedImages([]);
      this.isSelectActive = false;
    },

    // 이미지 다중선택
    async handleImageMultiSelect({ image, index }, event) {
      // Shift 키가 눌린 경우 다중 선택 모드
      // 범위 내 모든 이미지 토글
      const start = Math.min(this.selectedImageIndex, index);
      const end = Math.max(this.selectedImageIndex, index);

      const newSelectedImages = [...this.getSelectedImages];

      for (let i = start; i <= end; i++) {
        const rangeImage = this.getThermalImages[i];
        const isSelected = newSelectedImages.some(img => img.id === rangeImage.id);
        if(start==this.selectedImageIndex && start==i || end==this.selectedImageIndex && end==i){
          //이미 선택되어있음 아무런 동작x
        } else if (isSelected) {
          // 이미 선택된 이미지면 제거
          const removeIndex = newSelectedImages.findIndex(img => img.id === rangeImage.id);
          newSelectedImages.splice(removeIndex, 1);
        } else {
          newSelectedImages.push(rangeImage);
        }
      }

      this.setSelectedImages(newSelectedImages);
      this.selectedImageIndex = index;
      this.selectedImageId = image.id;
    },

    // 이미지 선택 토글 (일반 클릭)
    handleImageToggle({ image, index, isSelected }) {
      const newSelectedImages = isSelected
        ? this.getSelectedImages.filter(img => img.id !== image.id)
        : [...this.getSelectedImages, image];

      this.setSelectedImages(newSelectedImages);
      this.selectedImageIndex = index;
      this.selectedImageId = image.id;

      if (newSelectedImages.length === 0) {
        this.isShowActive = false;
      }

      console.log('[ImageAlignmentPage] 이미지 토글:', {
        imageId: image.id,
        action: isSelected ? '해제' : '선택',
        totalSelected: newSelectedImages.length
      });
    },

    // 이미지 편집
    handleImageEdit({ image, index }) {
      console.log('이미지 편집:', image, index);
    },
    // 마우스 진입
    handleMouseEnter(index) {
      this.hoveredImageIndex = index;
    },

    // 마우스 이탈
    handleMouseLeave() {
      this.hoveredImageIndex = -1;
    },

    handleImageListMoveUp({ index }) {
      if (index <= 0) {
        return;
      }
      const next = index - 1;
      const img = this.getThermalImages[next];
      this.selectedImageIndex = next;
      if (img) {
        this.selectedImageId = img.id;
      }
      this.hoveredImageIndex = next;
    },

    handleImageListMoveDown({ index }) {
      const images = this.getThermalImages;
      if (!images || index >= images.length - 1) {
        return;
      }
      const next = index + 1;
      const img = images[next];
      this.selectedImageIndex = next;
      if (img) {
        this.selectedImageId = img.id;
      }
      this.hoveredImageIndex = next;
    },

    // 사이트 선택 시 지도 이동
    async handleSelectSite(site) {
      this.tempSelectedReportId = null;
      await this.selectSite(site);

      // siteData store에서 사이트 상세 정보 가져오기
      const siteDetails = this.$store.getters['siteData/getSiteDetails'];

      if (siteDetails) {
        this.moveToSite(siteDetails);
      } else {
        console.warn('[ImageAlignmentPage] 사이트 상세 정보가 없습니다:', site);
        this.moveToSite(site);
      }
    },
    // 모든 선택 해제
    clearAllSelections() {
      this.setSelectedImages([]);
      this.selectedImageIndex = -1;
      this.selectedImageId = null;
      this.isShowActive = false;
      console.log('[ImageAlignmentPage] 모든 선택 해제');
    },

    // 선택된 이미지들의 영역으로 지도 이동
    centerMapOnSelectedImages(images) {
      if (!this.map || !images || images.length === 0) {
        return;
      }

      try {
        // 모든 이미지의 좌표를 수집
        const allCoordinates = [];

        images.forEach(image => {
          if (image.imgPosCoords && image.imgPosCoords.coordinates) {
            image.imgPosCoords.coordinates.forEach(coordSet => {
              if (coordSet && coordSet.length > 0) {
                allCoordinates.push(...coordSet);
              }else{
                console.warn('[ImageAlignmentPage] 선택된 이미지들에서 유효한 좌표를 찾을 수 없습니다.');
              }
            });
          }
        });
        // 전체 영역의 경계 계산
        const minLng = Math.min(...allCoordinates.map(coord => coord[0]));
        const maxLng = Math.max(...allCoordinates.map(coord => coord[0]));
        const minLat = Math.min(...allCoordinates.map(coord => coord[1]));
        const maxLat = Math.max(...allCoordinates.map(coord => coord[1]));

        // 중심점 계산
        const center = [(minLng + maxLng) / 2, (minLat + maxLat) / 2];

        // 영역 크기에 따른 줌 레벨 계산
        const lngDiff = maxLng - minLng;
        const latDiff = maxLat - minLat;
        const maxDiff = Math.max(lngDiff, latDiff);

        let zoom = 18; // 기본 줌 레벨
        if (maxDiff > 0.01) zoom = 16;
        if (maxDiff > 0.05) zoom = 14;
        if (maxDiff > 0.1) zoom = 12;
        if (maxDiff > 0.5) zoom = 10;

        console.log('[ImageAlignmentPage] 선택된 이미지들 영역으로 지도 이동:', {
          center: center,
          zoom: zoom,
          imageCount: images.length,
          coordinateCount: allCoordinates.length
        });

        // 지도 이동
        this.map.getView().animate({
          center: center,
          zoom: zoom
        });

      } catch (error) {
        console.error('[ImageAlignmentPage] 지도 이동 중 오류:', error);
      }
    }

  },

  created() {
    this.setLoading(true);
  },

  async mounted() {
    try {
      await this.initMap();
      const thermalImages = await this.$store.dispatch('imageAlignment/initializeImageAlignment');
      console.log('thermalImages:', thermalImages);
      console.log('getThermalImages', this.getThermalImages);

    } finally {
      this.setLoading(false);
    }
  },
  beforeUnmount() {
    this.cleanup();
  }
};
</script>

<style scoped>
.page-content {
  height: calc(100vh - var(--footerHeight) - 2 * var(--toolbarHeight));
  display: flex;
  flex-direction: column;
}

.main-content {
  display: flex;
  flex: 1;
  overflow: hidden;
}

.navigation-sidebar {
  width: 350px;
  min-width: 350px;
  height: calc(100vh - var(--footerHeight) - 2 * var(--toolbarHeight));
  border-right: 1px solid var(--v-secondary-base);
  background-color: var(--v-surface-base);
}

.sidebar_list {
  height: 100%;
  overflow-y: auto;
}

/* 지도 컨테이너 스타일 */
.map-container {
  flex: 1;
  position: relative;
  background-color: var(--v-background-base);
}

#image-alignment-map {
  width: 100%;
  height: 100%;
}

/* 선택된 이미지 개수 스타일 */
.selected-count {
  color: var(--v-info-base);
  font-weight: bold;
}

.clear-all-button {
  background-color: var(--v-secondary-base) !important;
  color: var(--v-text-base) !important;
}
/* 액션 버튼 통일성 스타일 */
.action-button {
  min-width: 100px;
  font-weight: 500;
  text-transform: none;
  letter-spacing: 0.5px;
  color: var(--v-text-base);
}

/* 지도 툴바 */
.map-toolbar {
  position: absolute;
  top: 10px;
  right: 10px;
  background: var(--v-surface-base);
  border-radius: 4px;
  padding: 8px;
  display: flex;
  align-items: center;
  z-index: 1000;
  box-shadow: 0 2px 4px color-mix(in srgb, var(--v-text-base) 12%, transparent);
}

/* 상태 패널 */
.status-panel {
  position: absolute;
  bottom: 10px;
  left: 10px;
  background: var(--v-surface-base);
  border-radius: 4px;
  z-index: 1000;
  box-shadow: 0 2px 4px color-mix(in srgb, var(--v-text-base) 12%, transparent);
  max-width: 300px;
}

/* 다크 테마 대응 */
.theme--dark .navigation-sidebar {
  background-color: var(--v-surface-base);
  border-right-color: var(--v-secondary-base);
}

.theme--dark .action-buttons-container {
  background-color: var(--v-surface-base);
  border-bottom-color: var(--v-secondary-base);
}

/* 반응형 디자인 */
@media (max-width: 768px) {
  .main-content {
    flex-direction: column;
  }

  .navigation-sidebar {
    width: 100%;
    min-width: auto;
    height: 300px;
    border-right: none;
    border-bottom: 1px solid var(--v-secondary-base);
  }

  .map-container {
    height: calc(100vh - 364px);
  }
}

/* 헤더·버튼 아래 남는 높이를 이미지 목록이 채움 (고정 calc 제거) */
.image-alignment-rail-stack {
  width: 100%;
  height: 100%;
  min-height: 0;
  display: flex;
  flex-direction: column;
}

.image-alignment-rail-stack__inner {
  width: 100%;
  height: 100%;
  min-height: 0;
  flex: 1;
}

.image-list-container {
  flex: 1 1 auto;
  min-height: 0;
  overflow: hidden;
  display: flex;
  flex-direction: column;
}

.image-alignment-rail-stack__empty {
  flex: 1 1 auto;
  min-height: 0;
  width: 100%;
  background-color: var(--v-surface-base);
}
</style>
